import { ChangeDetectionStrategy, Component, OnInit, computed, signal } from '@angular/core';
import { DashboardApi } from 'api/dashboard/dashboard-api.js';
import { AppointmentDb } from 'lib/cosmos/appointment/appointmentDb.js';
import { CosmosDb } from 'lib/cosmos/cosmosDb.js';
import { FeedDb } from 'lib/cosmos/feed/feedDb.js';
import { UserDb } from 'lib/cosmos/user/userDb.js';
import { linkedItemsHelper } from 'lib/helpers/linked-items-helper.class.js';
import { stringHelper } from 'lib/helpers/string-helper.class';
import { ListItem } from 'lib/ui/list/index.js';
import { Observable, map } from 'rxjs';
import { TypeCoreComponent } from 'src/app/pages/type-routes/type-core.component.js';

@Component({
	selector: 'mod-trainer-dashboard',
	templateUrl: './trainer-dashboard.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false
})
export class TrainerDashboardComponent extends TypeCoreComponent implements OnInit {
	private readonly trainerDashboardData = signal<DashboardApi.TrainerDashboardData | undefined>(undefined);
	private readonly linkedItems = signal<readonly CosmosDb.ICosmosItem[]>([]);

	public readonly helpdeskLink = computed<string | undefined>(() => {
		const authUser = this.authUser();

		if (!authUser) {
			return undefined;
		}
		return super.actionRoute((routes) => routes.helpdeskRoutes.conversation.children.form, authUser.id).getUrl();
	});

	public readonly latestActiveClients = computed<readonly ListItem<UserDb.IUserItem>[] | undefined>(() => {
		const dashboardData = this.trainerDashboardData();

		if (!dashboardData) {
			return undefined;
		}

		return dashboardData.latestActiveClients.map<ListItem<UserDb.IUserItem>>((client) => {
			return {
				data: client,
				id: client.id,
				title: client.fullName,
				avatar: client.avatar,
				subtitle: client.lastActiveDate ? super.fromNow(client.lastActiveDate) : 'Not yet active',
				routerLink: super
					.actionRoute((routes) => routes.trainerClientRoutes.client.children.view, client.id)
					.getUrl()
			};
		});
	});

	public readonly latestMessages = computed<readonly ListItem<FeedDb.IFeedItem>[] | undefined>(() => {
		const dashboardData = this.trainerDashboardData();

		if (!dashboardData) {
			return undefined;
		}

		return dashboardData.latestChatMessages.map<ListItem<FeedDb.IFeedItem>>((chatItem) => {
			const linkedClientItem = linkedItemsHelper.getLinkedItemById<UserDb.IUserItem>(
				this.linkedItems(),
				chatItem.originUserId
			);

			return {
				data: chatItem,
				id: chatItem.id,
				title: super.getRequiredItemName(linkedClientItem?.fullName),
				avatar: linkedClientItem?.avatar,
				subtitle: chatItem.message,
				routerLink: super
					.actionRoute((routes) => routes.chatRoutes.conversation.children.view, chatItem.originUserId)
					.getUrl(),
				fromNowDate: chatItem.system.created
			};
		});
	});

	public readonly latestFeeds = computed<readonly ListItem<FeedDb.IFeedItem>[] | undefined>(() => {
		const dashboardData = this.trainerDashboardData();

		if (!dashboardData) {
			return undefined;
		}

		return dashboardData.latestFeeds.map<ListItem<FeedDb.IFeedItem>>((feedItem) => {
			const linkedUser = linkedItemsHelper.getLinkedItemById<UserDb.IUserItem>(
				this.linkedItems(),
				feedItem.originUserId
			);

			const messageWithoutHtml = stringHelper.stripHtmlTags(feedItem.message);

			return {
				data: feedItem,
				id: feedItem.id,
				title: feedItem.system.name,
				avatar: linkedUser?.avatar,
				subtitle: messageWithoutHtml,
				select: () => {
					this.dependencies.dialogService.openTextDialog({
						title: feedItem.system.name,
						size: 'default',
						text: feedItem.message,
						tableItems: [
							{
								title: 'Sent',
								value: super.formatDateTime(feedItem.system.created),
								valueType: 'text'
							}
						]
					});
				},
				fromNowDate: feedItem.system.created
			};
		});
	});

	public readonly upcomingAppointments = computed<readonly ListItem<AppointmentDb.IAppointmentItem>[] | undefined>(
		() => {
			const dashboardData = this.trainerDashboardData();

			if (!dashboardData) {
				return undefined;
			}

			return dashboardData.upcomingAppointments.map<ListItem<AppointmentDb.IAppointmentItem>>((appointment) => {
				const linkedClientItem = linkedItemsHelper.getLinkedItemById<UserDb.IUserItem>(
					this.linkedItems(),
					appointment.clientId
				);

				return {
					data: appointment,
					id: appointment.id,
					title: appointment.system.name,
					avatar: linkedClientItem?.avatar,
					subtitle: super.formatDateTime(appointment.dateTime),
					routerLink: super.actionRoute((routes) => routes.calendarRoutes.view.children.upcoming).getUrl()
				};
			});
		}
	);

	constructor() {
		super();

		this.dataInit = () => {
			super.subscribeToObservable(this.initTrainerDashboardData());
		};
	}

	private initTrainerDashboardData(): Observable<void> {
		return this.dependencies.httpService
			.get<DashboardApi.TrainerDashboardData>((builder) =>
				builder.withCustomApiRoute('dashboard', 'getTrainerDashboardData').build()
			)
			.pipe(
				map((response) => {
					this.trainerDashboardData.set(response.data);
					this.linkedItems.set(response.linkedItems ?? []);
				})
			);
	}
}
